// utils
import { buildAttendeeItems, formatValue } from "./attendeeItems";
import { formatDocument, formatMobile } from "./formatters";

// Monta as abas "Produtos" e "Participantes" da exportação XLSX. Toda linha leva o ID da
// venda e o e-mail do comprador, para o operador cruzar com a aba "Vendas".

// rótulo em texto puro para o cabeçalho da planilha (rótulos de termos vêm com HTML)
export function plainLabel(label) {
  return String(label ?? "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/\s+/g, " ")
    .trim();
}

const money = (value) => {
  const number = Number(value);
  return Number.isNaN(number) ? null : number;
};
const MONEY_FORMAT = '"R$" #,##0.00';

export const productsSchema = [
  { column: "ID da venda", type: String, value: (p) => p.transaction_id },
  { column: "E-mail do comprador", type: String, value: (p) => p.customer_email ?? "" },
  { column: "Produto", type: String, value: (p) => p.description ?? "" },
  { column: "Descrição", type: String, value: (p) => p.description2 ?? "" },
  { column: "Quantidade", type: Number, value: (p) => Number(p.quantity) || 0 },
  { column: "Valor unitário", type: Number, format: MONEY_FORMAT, value: (p) => money(p.unit_price) },
  { column: "Subtotal", type: Number, format: MONEY_FORMAT, value: (p) => money(p.subtotal) },
  { column: "Desconto da venda", type: Number, format: MONEY_FORMAT, value: (p) => money(p.promo_off) },
  { column: "Cupom", type: String, value: (p) => (p.promo_code && p.promo_code !== "NOCODE" ? p.promo_code : "") },
  { column: "Total da venda", type: Number, format: MONEY_FORMAT, value: (p) => money(p.amount) },
];

// Colunas fixas + uma coluna por campo do formulário do evento (na ordem configurada) +
// propriedades extras encontradas nos participantes, com rótulo humanizado.
export function buildAttendeesSheet(attendees = [], fields = []) {
  const rows = attendees.map((attendee) => {
    const info = attendee.info || {};
    const items = buildAttendeeItems(info, fields);
    const values = Object.fromEntries(items.map((item) => [item.name, item.value]));
    return {
      transaction_id: attendee.transaction_id,
      customer_email: attendee.customer_email ?? "",
      product_description: attendee.product_description ?? "",
      mice_id: attendee.mice_id ?? "",
      name: formatValue(info.name),
      email: formatValue(info.email),
      document: formatDocument(info.document ?? info.cpf ?? ""),
      mobile: formatMobile(info.mobile ?? ""),
      values,
      labels: Object.fromEntries(items.map((item) => [item.name, item.label])),
    };
  });

  // campos já cobertos pelas colunas fixas não repetem
  const fixed = new Set(["name", "email", "document", "cpf", "mobile"]);
  const dynamic = [];
  const seen = new Set();
  fields.forEach((field) => {
    if (fixed.has(field.name) || seen.has(field.name)) return;
    if (!rows.some((row) => field.name in row.values)) return;
    seen.add(field.name);
    dynamic.push({ name: field.name, label: plainLabel(field.label) || field.name });
  });
  rows.forEach((row) => {
    Object.keys(row.values)
      .filter((name) => !fixed.has(name) && !seen.has(name))
      .sort()
      .forEach((name) => {
        seen.add(name);
        dynamic.push({ name, label: plainLabel(row.labels[name]) || name });
      });
  });

  const schema = [
    { column: "ID da venda", type: String, value: (r) => r.transaction_id },
    { column: "E-mail do comprador", type: String, value: (r) => r.customer_email },
    { column: "Produto do participante", type: String, value: (r) => r.product_description },
    { column: "ID MICE", type: String, value: (r) => String(r.mice_id ?? "") },
    { column: "Nome", type: String, value: (r) => r.name },
    { column: "E-mail", type: String, value: (r) => r.email },
    { column: "CPF", type: String, value: (r) => r.document },
    { column: "Celular", type: String, value: (r) => r.mobile },
    ...dynamic.map((column) => ({
      column: column.label,
      type: String,
      value: (r) => r.values[column.name] ?? "",
    })),
  ];

  return { schema, rows };
}
